import React, { useEffect, useMemo, useRef, useState } from "react";
import { hasPriceRange } from "@/lib/variants";
import { homeSets } from "@/lib/homesets";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ChevronLeft, ChevronRight, Cross, Pill, Truck, ShieldCheck, HeartPulse, Droplets, Star, Quote, BadgeCheck, Stethoscope, PackageCheck, Syringe, FileText } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { useCart } from "@/context/CartContext";
import StoreShell from "@/components/StoreShell";
import { Newsletter } from "@/components/pharmacy/PharmacyParts";
import { ManagedReviews, ManagedInstagram } from "@/components/storefront/HomeSections";
import { Reveal, useDayCountdown, CountUp } from "@/components/pharmacy/hooks";
import { inr, rating, reviews, shade, hexA } from "@/lib/storeutils";

const SLIDES = [
  { title: "Take Care of", title2: "Your Health Now.", sub: "Genuine medicines & healthcare essentials, delivered fast.", cta: "Shop Now" },
  { title: "Trusted Medicines,", title2: "Right At Your Door.", sub: "Upload your prescription at checkout and we do the rest.", cta: "Browse Medicines" },
  { title: "Wellness Starts", title2: "With Small Steps.", sub: "Vitamins, devices and daily care — all in one place.", cta: "Explore Offers" },
];
const CAPSULES = [["8%", "12%", 38, 0], ["78%", "8%", 28, 1.2], ["88%", "62%", 34, .6], ["6%", "70%", 26, 1.8], ["52%", "88%", 30, 2.4]];

export default function PharmacyHome() {
  const s = useStore();
  const cart = useCart();
  const [params] = useSearchParams();
  const q = (params.get("q") || "").toLowerCase();
  const catParam = params.get("cat") || "";
  const [activeCat, setActiveCat] = useState(catParam || "All");
  useEffect(() => { if (catParam) setActiveCat(catParam); }, [catParam]);
  useEffect(() => { if ((q || catParam) && !s.loading) document.getElementById("shop")?.scrollIntoView({ behavior: "smooth" }); }, [q, catParam, s.loading]);

  const filtered = useMemo(() => s.products.filter((p) => (activeCat === "All" || p.category === activeCat) && p.name.toLowerCase().includes(q)), [s.products, activeCat, q]);
  if (s.loading || !s.store) return <div className="min-h-screen flex items-center justify-center"><span className="w-10 h-10 rounded-full border-4 border-slate-200 border-t-emerald-500 animate-spin" /></div>;
  if (s.error) return <div className="min-h-screen flex items-center justify-center text-slate-500">{s.error}</div>;

  const { c } = s;
  const featured = s.products.filter((p) => p.featured);
  const spotlight = (featured.length ? featured : s.products);
  // three shelves with different products: new arrivals, special (featured), deal of the day (discounted) (see lib/homesets.js)
  const sets = homeSets(s.products, { fresh: 4, featured: 8, sale: 6 });
  const catTiles = s.categories.map((name) => ({ name, image: s.products.find((p) => p.category === name)?.image, count: s.products.filter((p) => p.category === name).length }));
  const cats = ["All", ...s.categories];


  return (
    <StoreShell>
      <Hero s={s} product={spotlight[0]} />
      <PromoPair s={s} a={spotlight[0]} b={spotlight[1] || spotlight[0]} />
      {catTiles.length > 0 && <CategoryTiles s={s} tiles={catTiles} onPick={(n) => { setActiveCat(n); document.getElementById("shop")?.scrollIntoView({ behavior: "smooth" }); }} />}
      {sets.sale.length > 0 && <DealOfTheDay s={s} cart={cart} deals={sets.sale} />}
      <FeatureBanners s={s} product={spotlight[2] || spotlight[0]} coupon={s.coupons[0]} />
      {sets.fresh.length > 0 && <NewArrivals s={s} cart={cart} items={sets.fresh} />}

      <section id="shop" className="max-w-6xl mx-auto px-5 py-14">
        <Reveal><h2 className="text-center text-[26px] font-medium">Our Special Products</h2></Reveal>
        <Reveal className="flex gap-2 flex-wrap justify-center mt-6 mb-8">
          {cats.map((cat) => <button key={cat} onClick={() => setActiveCat(cat)} data-testid={`storefront-cat-${cat}`} className="px-4 py-1.5 rounded-full text-[13px] font-medium transition-all hover:-translate-y-0.5" style={activeCat === cat ? { background: c.primary, color: "#fff" } : { background: "#f3f5f6", color: "#111" }}>{cat}</button>)}
        </Reveal>
        {filtered.length === 0 ? <div className="text-center py-16 text-slate-500">No products found.</div> : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="storefront-products">
            {(activeCat === "All" && !q && sets.featured.length ? sets.featured : filtered).map((p, i) => <Reveal key={p.id} kind="rv-zoom" delay={(i % 4) * 90}><PharmaCard p={p} s={s} cart={cart} /></Reveal>)}
          </div>
        )}
      </section>

      <ShippingStrip c={c} />
      <Stats c={c} s={s} />
      <ManagedReviews s={s} layout="pharmacy" fallback={<Testimonials c={c} />} />
      <ManagedInstagram s={s} layout="pharmacy" />
      <HealthTips c={c} />
      <Newsletter c={c} />
      <Trust c={c} />
      <Marquee c={c} cats={s.categories} />
    </StoreShell>
  );
}

/* ---------------- New arrivals ---------------- */
function NewArrivals({ s, cart, items }) {
  return (
    <section id="new-arrivals" className="max-w-6xl mx-auto px-5 pt-4 pb-6" data-testid="pharmacy-new-arrivals">
      <Reveal><h2 className="text-center text-[26px] font-medium">New Arrivals</h2></Reveal>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
        {items.map((p, i) => <Reveal key={p.id} kind="rv-zoom" delay={(i % 4) * 90}><PharmaCard p={p} s={s} cart={cart} /></Reveal>)}
      </div>
    </section>
  );
}

/* ---------------- Hero ---------------- */
function Hero({ s, product }) {
  const { c } = s;
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => { if (paused) return undefined; const t = setInterval(() => setI((x) => (x + 1) % SLIDES.length), 5500); return () => clearInterval(t); }, [paused]);
  const sl = SLIDES[i];
  return (
    <section className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${c.primary}, ${shade(c.primary, 35)} 60%, #d9f7ee)`, minHeight: 440 }} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-testid="pharmacy-hero">
      <span className="absolute -right-24 -top-24 w-[420px] h-[420px] rounded-full rx-spin-slow" style={{ border: "2px dashed rgba(255,255,255,.35)" }} />
      <span className="absolute right-24 bottom-0 w-72 h-72 rounded-full rx-float-slow" style={{ background: "rgba(255,255,255,.16)" }} />
      {CAPSULES.map(([x, y, sz, d], k) => <Pill key={k} size={sz} className="absolute rx-float hidden sm:block" style={{ left: x, top: y, color: "rgba(255,255,255,.75)", animationDelay: `${d}s` }} />)}
      <div className="relative max-w-6xl mx-auto px-5 py-16 md:py-20 grid md:grid-cols-2 gap-8 items-center">
        <div key={i} className="text-white">
          <div className="rx-slide-up text-[12px] font-semibold tracking-[.3em] uppercase opacity-90 flex items-center gap-2"><Cross size={14} /> {s.store.business_category}</div>
          <h1 className="rx-slide-up mt-4 text-4xl md:text-[56px] leading-[1.08] font-light" style={{ animationDelay: ".1s" }}>{sl.title}<br /><span className="font-semibold rx-shimmer-text">{sl.title2}</span></h1>
          <p className="rx-slide-up mt-4 text-[15px] max-w-md opacity-95" style={{ animationDelay: ".2s" }}>{sl.sub}</p>
          <a href="#shop" data-testid="storefront-shop-collection" className="rx-slide-up rx-btn mt-7 inline-flex items-center gap-2 px-7 py-3 text-[12px] font-bold tracking-widest uppercase" style={{ background: "#111", color: "#fff", animationDelay: ".3s" }}>{sl.cta} <ArrowRight size={14} /></a>
        </div>
        <div className="relative flex justify-center items-center h-[300px] md:h-[360px]">
          <span className="rx-ring w-64 h-64" style={{ color: "rgba(255,255,255,.7)" }} /><span className="rx-ring w-64 h-64" style={{ color: "rgba(255,255,255,.7)", animationDelay: "1.5s" }} />
          <div key={`img${i}`} className="rx-slide-in relative w-60 h-60 md:w-72 md:h-72 rounded-full overflow-hidden rx-float bg-white" style={{ boxShadow: "0 30px 60px rgba(0,0,0,.25)", border: "8px solid rgba(255,255,255,.85)" }}>
            {product?.image ? <img src={product.image} alt={product.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center" style={{ color: c.primary }}><Cross size={90} className="rx-beat" /></div>}
          </div>
          <span className="absolute top-6 right-4 md:right-10 px-3 py-1.5 text-[12px] font-bold rx-beat" style={{ background: c.accent, color: "#fff", transform: "rotate(8deg)" }}>SALE</span>
          <span className="absolute bottom-8 left-2 md:left-6 flex items-center gap-2 bg-white rounded-xl px-3 py-2 text-[12px] font-semibold shadow-lg rx-float-slow" style={{ color: "#111" }}><ShieldCheck size={16} style={{ color: c.primary }} /> 100% Genuine</span>
        </div>
      </div>
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2">
        {SLIDES.map((_, k) => <button key={k} aria-label={`Slide ${k + 1}`} onClick={() => setI(k)} className="h-2 rounded-full transition-all duration-500" style={{ width: i === k ? 28 : 8, background: i === k ? "#fff" : "rgba(255,255,255,.5)" }} />)}
      </div>
    </section>
  );
}

/* ---------------- Promo pair ---------------- */
function PromoPair({ s, a, b }) {
  const { c, slug } = s;
  const items = [[a, "Best Sellers", "#f1f3f4", c.primary], [b, "Top Picks", "#e9eceb", c.accent]];
  return (
    <section className="max-w-6xl mx-auto px-5 -mt-1">
      <div className="grid md:grid-cols-2">
        {items.map(([p, tag, bg, btn], k) => p && (
          <Reveal key={k} kind={k ? "rv-right" : "rv-left"}>
            <Link to={`/store/${slug}/product/${p.id}`} className="rx-zoom group flex items-center justify-between gap-4 p-8 h-full overflow-hidden relative" style={{ background: bg }}>
              <div className="relative z-10">
                <div className="text-[10px] tracking-[.25em] uppercase text-slate-500">{tag}</div>
                <h3 className="text-2xl md:text-[28px] font-medium mt-2 leading-tight line-clamp-2 max-w-[200px]">{p.name}</h3>
                <span className="rx-btn inline-block mt-4 text-[11px] font-bold px-3 py-1.5" style={{ background: btn, color: "#fff" }}>From {inr(p.price)}</span>
              </div>
              <div className="w-40 h-40 md:w-48 md:h-48 shrink-0 rounded-full overflow-hidden bg-white rx-float" style={{ animationDelay: `${k * .8}s` }}>{p.image && <img src={p.image} alt={p.name} className="w-full h-full object-cover" />}</div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ---------------- Categories ---------------- */
function CategoryTiles({ s, tiles, onPick }) {
  const { c } = s;
  return (
    <section className="max-w-6xl mx-auto px-5 py-14">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {tiles.map((t, k) => (
          <Reveal key={t.name} kind="rv-zoom" delay={k * 80}>
            <button onClick={() => onPick(t.name)} data-testid={`storefront-cat-tile-${t.name}`} className="rx-card rx-zoom w-full rounded-lg border bg-white p-5 flex flex-col items-center gap-4" style={{ borderColor: "#eef0f1" }}>
              <div className="w-28 h-28 rounded-full overflow-hidden" style={{ background: hexA(c.primary, .1) }}>{t.image ? <img src={t.image} alt={t.name} className="w-full h-full object-cover" /> : <Pill className="m-auto mt-9" style={{ color: c.primary }} />}</div>
              <div className="text-[13px] font-semibold">{t.name}</div>
              <div className="text-[11px] text-slate-400 -mt-3">{t.count} items</div>
            </button>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ---------------- Deal of the day ---------------- */
function DealOfTheDay({ s, cart, deals }) {
  const { c } = s;
  const t = useDayCountdown();
  const rail = useRef(null);
  const go = (d) => rail.current?.scrollBy({ left: d * 280, behavior: "smooth" });
  const pad = (n) => String(n).padStart(2, "0");
  return (
    <section id="offers" className="max-w-6xl mx-auto px-5 pb-14">
      <Reveal className="relative rounded-2xl border-2 px-5 pb-6 pt-8" style={{ borderColor: hexA(c.primary, .55) }}>
        <div className="absolute -top-4 left-5 bg-white px-3 flex items-center gap-3">
          <h2 className="text-[19px] font-medium">Deal Of The Day</h2>
          <span className="text-[11px] text-slate-500">Ends in:</span>
          {[t.h, t.m, t.s].map((n, k) => <span key={k} className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold text-white tabular-nums" style={{ background: k === 2 ? c.accent : c.primary }}>{pad(n)}</span>)}
        </div>
        <button onClick={() => go(-1)} aria-label="Previous" className="absolute left-1 top-1/2 z-10 w-8 h-8 rounded-full bg-white shadow flex items-center justify-center hover:scale-110 transition-transform"><ChevronLeft size={16} /></button>
        <button onClick={() => go(1)} aria-label="Next" className="absolute right-1 top-1/2 z-10 w-8 h-8 rounded-full bg-white shadow flex items-center justify-center hover:scale-110 transition-transform"><ChevronRight size={16} /></button>
        <div ref={rail} className="flex gap-4 overflow-x-auto scroll-smooth pb-2" style={{ scrollSnapType: "x mandatory", scrollbarWidth: "none" }}>
          {deals.map((p) => <div key={p.id} className="shrink-0 w-[220px]" style={{ scrollSnapAlign: "start" }}><PharmaCard p={p} s={s} cart={cart} compact /></div>)}
        </div>
      </Reveal>
    </section>
  );
}

/* ---------------- Feature banners ---------------- */
function FeatureBanners({ s, product, coupon }) {
  const { c, slug } = s;
  return (
    <section className="max-w-6xl mx-auto px-5 pb-6 grid md:grid-cols-2 gap-5">
      <Reveal kind="rv-left"><a href="#shop" className="rx-zoom relative block h-56 overflow-hidden rounded-sm text-white" style={{ background: shade(c.primary, -30) }}>
        {product?.image && <img src={product.image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />}
        <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, ${shade(c.primary, -35)}ee, transparent)` }} />
        <div className="relative p-8"><h3 className="text-3xl font-light">Medicine<br />Family</h3><p className="text-[12px] mt-2">Only {product ? inr(product.price) : "₹20"}</p><span className="rx-btn inline-block mt-4 px-4 py-2 text-[11px] font-bold" style={{ background: c.accent }}>SHOP NOW</span></div>
      </a></Reveal>
      <Reveal kind="rv-right"><a href={`/store/${slug}#shop`} className="relative block h-56 overflow-hidden rounded-sm text-white" style={{ background: c.primary }}>
        <HeartPulse size={200} className="absolute -right-6 -bottom-8 rx-beat" style={{ color: "rgba(255,255,255,.2)" }} />
        <div className="relative p-8"><span className="inline-block bg-white text-[11px] font-bold px-2.5 py-1 rx-pulse" style={{ color: "#111" }}>{coupon ? (coupon.discount_type === "percent" ? `Offer ${coupon.value}%` : `Save ₹${coupon.value}`) : "Offer 50%"}</span>
          <div className="text-[10px] tracking-[.25em] uppercase mt-5 opacity-90">Medical Instruments</div><h3 className="text-3xl font-light mt-1">Care Essentials</h3>{coupon && <p className="text-[12px] mt-2">Use code <b>{coupon.code}</b></p>}</div>
      </a></Reveal>
    </section>
  );
}

/* ---------------- Product card ---------------- */
export function PharmaCard({ p, s, cart, compact }) {
  const { c, slug } = s;
  const off = p.compare_at_price ? Math.round((1 - p.price / p.compare_at_price) * 100) : 0;
  const hasVariants = p.variants?.length > 0 || p.variant_items?.length > 0;
  const [added, setAdded] = useState(false);
  const add = () => {
    if (hasVariants) { window.location.href = `/store/${slug}/product/${p.id}`; return; }
    cart.add(p, null, 1); toast.success("Added to cart"); setAdded(true); setTimeout(() => setAdded(false), 1200);
  };
  return (
    <div data-testid={`storefront-product-${p.id}`} className="rx-card rx-zoom group h-full flex flex-col bg-white border rounded-md overflow-hidden" style={{ borderColor: "#eef0f1" }}>
      <Link to={`/store/${slug}/product/${p.id}`} className="relative block overflow-hidden" style={{ background: "#f6f8f8", height: compact ? 160 : 190 }}>
        {p.image ? <img src={p.image} alt={p.name} className="w-full h-full object-cover" /> : <Pill className="m-auto mt-16 text-slate-300" size={40} />}
        {p.featured && <span className="absolute top-2 left-2 text-[10px] font-bold px-2 py-0.5 text-white rx-pulse" style={{ background: "#f59e0b" }}>HOT</span>}
        {off > 0 && <span className="absolute top-2 right-2 text-[10px] font-bold px-2 py-0.5 text-white" style={{ background: c.accent }}>-{off}%</span>}
        {p.stock <= 0 && <span className="absolute bottom-2 left-2 text-[10px] font-bold px-2 py-0.5 bg-slate-800 text-white">Sold out</span>}
        <span className="absolute inset-x-0 bottom-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300 text-center text-[11px] font-bold py-2 text-white" style={{ background: hexA("#000", .75) }}>QUICK VIEW</span>
      </Link>
      <div className="p-4 flex flex-col flex-1">
        <div className="text-[9px] tracking-[.2em] uppercase text-slate-400">{p.category || "Healthcare"}</div>
        <Link to={`/store/${slug}/product/${p.id}`}><h3 className="text-[13px] font-medium mt-1 line-clamp-1 transition-colors group-hover:text-emerald-600">{p.name}</h3></Link>
        <div className="flex items-center gap-1 mt-1">{[...Array(5)].map((_, i) => <Star key={i} size={10} fill={i < Math.round(rating(p.id)) ? "#f59e0b" : "none"} color="#f59e0b" />)}<span className="text-[10px] text-slate-400 ml-1">({reviews(p.id)})</span></div>
        <div className="flex items-baseline gap-2 mt-1.5">{hasPriceRange(p) && <span className="text-[11px] text-slate-400">From</span>}<span className="text-[13px] font-semibold" style={{ color: off ? c.accent : "#111" }}>{inr(p.price)}</span>{p.compare_at_price ? <span className="text-[11px] line-through text-slate-400">{inr(p.compare_at_price)}</span> : null}</div>
        <button onClick={add} disabled={p.stock <= 0} data-testid={`storefront-add-to-cart-btn-${p.id}`} className="rx-btn mt-3 w-full py-2 text-[11px] font-bold tracking-wider uppercase disabled:opacity-50" style={{ background: added ? "#16a34a" : c.primary, color: "#fff" }}>
          {added ? "✓ Added" : hasVariants ? "Select options" : "Add to cart"}
        </button>
      </div>
    </div>
  );
}

/* ---------------- Strips and sections ---------------- */
function ShippingStrip({ c }) {
  return (
    <Reveal kind="rv-zoom" className="max-w-6xl mx-auto px-5">
      <div className="relative overflow-hidden rounded-lg flex items-center justify-center gap-3 py-5 text-[13px] font-medium" style={{ background: `linear-gradient(90deg, ${hexA(c.primary, .12)}, ${hexA(c.primary, .28)})` }}>
        <ShieldCheck size={18} style={{ color: c.primary }} className="rx-beat" /> <b>100% Free Shipping</b> when ordering online
        <Truck size={20} style={{ color: c.primary }} className="rx-float" />
      </div>
    </Reveal>
  );
}

function Stats({ c, s }) {
  const items = [[Stethoscope, 100, "%", "Verified pharmacists"], [PackageCheck, Math.max(s.products.length, 1), "+", "Products in store"], [HeartPulse, 24, "×7", "Online ordering"], [Syringe, 100, "%", "Genuine medicines"]];
  return (
    <section className="max-w-6xl mx-auto px-5 py-14 grid grid-cols-2 md:grid-cols-4 gap-4">
      {items.map(([I, n, suf, l], k) => (
        <Reveal key={l} delay={k * 100} className="text-center rounded-lg p-6" style={{ background: hexA(c.primary, .07) }}>
          <I size={26} className="mx-auto rx-float" style={{ color: c.primary, animationDelay: `${k * .4}s` }} />
          <div className="text-3xl font-semibold mt-2"><CountUp to={n} suffix={suf} /></div><div className="text-[12px] text-slate-500 mt-1">{l}</div>
        </Reveal>
      ))}
    </section>
  );
}

function Testimonials({ c }) {
  const t = [["Taylor Senna", "Quick delivery and genuine medicines. The prescription upload was effortless."], ["Monika Alizalee", "Pharmacist called to confirm my order. Great, trustworthy service."], ["Rajarshi Sarkar", "Good prices on daily supplements and well packed. Will order again."]];
  return (
    <section className="max-w-6xl mx-auto px-5 pb-14">
      <Reveal><h2 className="text-center text-[26px] font-medium mb-8">Our Client Says</h2></Reveal>
      <div className="grid md:grid-cols-3 gap-5">
        {t.map(([n, x], k) => (
          <Reveal key={n} delay={k * 120}><div className="rx-card h-full rounded-md p-7" style={{ background: "#f6f8f8" }}>
            <Quote size={24} style={{ color: c.primary }} /><p className="text-[13px] text-slate-600 mt-3 leading-relaxed">{x}</p>
            <div className="flex items-center gap-3 mt-5"><span className="w-9 h-9 rounded-full flex items-center justify-center text-white text-[13px] font-bold" style={{ background: c.primary }}>{n[0]}</span><div><div className="text-[13px] font-semibold">{n}</div><div className="flex">{[...Array(5)].map((_, i) => <Star key={i} size={10} fill="#f59e0b" color="#f59e0b" />)}</div></div></div>
          </div></Reveal>
        ))}
      </div>
    </section>
  );
}

function HealthTips({ c }) {
  const tips = [[HeartPulse, "Heart health: small daily habits that help", "Walk, hydrate and know your numbers.", c.primary], [Droplets, "Why staying hydrated matters", "Water supports energy, focus and recovery.", "#0ea5e9"], [Pill, "Taking medicines safely", "Follow your doctor's dose and store them properly.", c.accent]];
  return (
    <section className="max-w-6xl mx-auto px-5 pb-14">
      <Reveal><h2 className="text-center text-[26px] font-medium mb-8">Health Tips</h2></Reveal>
      <div className="grid md:grid-cols-3 gap-5">
        {tips.map(([I, t, d, col], k) => (
          <Reveal key={t} delay={k * 120}><div className="rx-card rx-zoom group overflow-hidden rounded-md border bg-white" style={{ borderColor: "#eef0f1" }}>
            <div className="h-40 flex items-center justify-center relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${hexA(col, .22)}, ${hexA(col, .06)})` }}>
              <span className="absolute w-40 h-40 rounded-full rx-float-slow" style={{ background: hexA(col, .12) }} /><I size={56} className="relative rx-float transition-transform group-hover:scale-125" style={{ color: col }} />
            </div>
            <div className="p-5"><div className="text-[10px] tracking-widest text-slate-400 uppercase">Wellness</div><h3 className="text-[15px] font-medium mt-1">{t}</h3><p className="text-[12px] text-slate-500 mt-2">{d}</p></div>
          </div></Reveal>
        ))}
      </div>
    </section>
  );
}

function Trust({ c }) {
  const t = [[BadgeCheck, "Only 100% verified doctor", "Prescriptions are checked by a qualified pharmacist."], [ShieldCheck, "Help and access in our mission", "Reach us any time through order tracking."], [Truck, "Verified drug delivery", "Sealed, safe packaging on every order."]];
  return (
    <section className="max-w-6xl mx-auto px-5 py-14 grid md:grid-cols-3 gap-5">
      {t.map(([I, h, d], k) => <Reveal key={h} delay={k * 100}><div className="rx-card flex gap-4 items-start rounded-md border p-6 bg-white" style={{ borderColor: "#eef0f1" }}><I size={34} className="shrink-0 rx-beat" style={{ color: c.primary, animationDelay: `${k * .5}s` }} /><div><div className="text-[14px] font-semibold">{h}</div><p className="text-[12px] text-slate-500 mt-1">{d}</p></div></div></Reveal>)}
    </section>
  );
}

function Marquee({ c, cats }) {
  const words = [...(cats.length ? cats : ["Medicines", "Wellness", "Care"]), "Genuine Medicines", "Fast Delivery", "Prescription Verified", "24×7 Ordering"];
  const row = [...words, ...words];
  return (
    <div className="overflow-hidden py-7 border-t" style={{ background: "#fafbfb", borderColor: "#eef0f1" }}>
      <div className="rx-marquee">{row.map((w, i) => <span key={i} className="mx-8 text-xl font-semibold uppercase tracking-widest whitespace-nowrap" style={{ color: i % 2 ? hexA(c.primary, .55) : "#cbd2d6" }}>{w}</span>)}</div>
    </div>
  );
}
